"use client"
import { type RouterOutputs, api } from "@/components/TRPCProvider"
import { TooltipIconButton } from "@/components/TooltipIconButton"
import { Avatar } from "@/components/ui/Avatar"
import { Button } from "@/components/ui/Button"
import { IconButton } from "@/components/ui/IconButton"
import { Popover, PopoverContent, PopoverHeader, PopoverTrigger } from "@/components/ui/Popover"
import { Spinner } from "@/components/ui/Spinner"
import { Tooltip } from "@/components/ui/Tooltip"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { capitalize, createFullName } from "@/lib/helpers/text"
import { useLocalStorage } from "@/lib/hooks/useLocalStorage"
import { createAssetUrl } from "@/lib/s3"
import { ACCOUNT_NOTIFICATION_TYPES, CONTENT_NOTIFICATION_TYPES, PROJECT_NOTIFICATION_TYPES } from "@/lib/static/notification"
import { ROLE } from "@/lib/static/role"
import { TRENDBABY_TOOL_IDS } from "@/lib/static/trendbaby-tool-ids"
import { cn } from "@/lib/tailwind"
import type { Role } from "@prisma/client"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import {
  Archive,
  Bell,
  BellDot,
  Check,
  CheckSquare2,
  Ellipsis,
  FilterIcon,
  Folder,
  Inbox,
  Lightbulb,
  SquareDashed,
  Undo2,
  User,
} from "lucide-react"
import Link from "next/link"
import * as React from "react"

dayjs.extend(relativeTime)

export function Notifications() {
  const { data } = api.notification.unreadCount.useQuery(undefined, { staleTime: 1000 * 60 * 15, refetchInterval: 1000 * 120 })
  return (
    <Popover>
      <Tooltip label="Notifications">
        <PopoverTrigger asChild>
          <IconButton
            className="tour-home-notifications size-9"
            icon={
              <div className="relative">
                <Bell size={16} />
                {data ? (
                  <span className="-top-3 -right-3 absolute flex h-5 w-5 items-center justify-center rounded-full bg-red-500 font-bold text-white text-xxs">
                    {data}
                  </span>
                ) : null}
              </div>
            }
            aria-label="Notifications"
            variant="outline"
          />
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent align="end" alignOffset={10} sideOffset={8}>
        <NotificationBody />
      </PopoverContent>
    </Popover>
  )
}

type Type = "PROJECT" | "ACCOUNT" | "CONTENT"
type Status = "UNREAD_AND_READ" | "UNREAD" | "ARCHIVED"

function NotificationBody() {
  const [type, setType] = React.useState<Type | null>(null)
  const [status, setStatus] = useLocalStorage<Status>("notification-status", "UNREAD_AND_READ")

  const [isRefetching, startRefetch] = React.useTransition()

  const statuses =
    status === "UNREAD_AND_READ"
      ? ["UNREAD", "READ", "SEEN"]
      : status === "UNREAD"
        ? ["UNREAD", "SEEN"]
        : status === "ARCHIVED"
          ? ["ARCHIVED"]
          : []

  const types =
    type === "ACCOUNT"
      ? ACCOUNT_NOTIFICATION_TYPES
      : type === "CONTENT"
        ? CONTENT_NOTIFICATION_TYPES
        : type === "PROJECT"
          ? PROJECT_NOTIFICATION_TYPES
          : []

  const { data, isLoading, isError } = api.notification.all.useQuery({ types, statuses, skip: 0 }, { staleTime: 1000 * 60 })
  const { data: seenCount } = api.notification.seenCount.useQuery(undefined, { staleTime: 1000 * 5 })

  const [items, setItems] = React.useState(data?.items)

  React.useEffect(() => {
    setItems(data?.items)
  }, [data?.items])

  const utils = api.useUtils()

  const handleFetchMore = React.useCallback(async () => {
    if (!items?.length) return

    startRefetch(async () => {
      const newItems = (await utils.notification.all.fetch({ types, statuses, skip: items.length }).catch()) || []
      setItems([...items, ...newItems.items])
    })
  }, [items, types])

  const { mutate: markAllAsRead } = api.notification.markAllAsRead.useMutation({
    onSuccess: () => {
      utils.notification.all.setData({ types, statuses, skip: 0 }, (prev) =>
        prev ? { count: prev.count, items: prev.items.map((n) => ({ ...n, status: "READ" })) } : prev,
      )
      utils.notification.seenCount.setData(undefined, 0)
    },
  })
  const { mutate: markAllAsSeen } = api.notification.markAllAsSeen.useMutation({
    onSuccess: () => {
      utils.notification.unreadCount.setData(undefined, 0)
    },
  })
  const { mutate: markAllAsArchived } = api.notification.markAllAsArchived.useMutation({
    onSuccess: () => {
      utils.notification.all.invalidate()
      utils.notification.seenCount.invalidate()
      utils.notification.unreadCount.invalidate()
    },
  })

  React.useEffect(() => {
    markAllAsSeen()
  }, [])

  return (
    <>
      <PopoverHeader>
        Notifications
        {seenCount ? <span className="pl-2 font-bold text-red-500 text-sm">{`${seenCount} new`}</span> : null}
      </PopoverHeader>

      <div className="h-[450px] w-[500px] p-2 pb-12">
        <div className="mb-2 flex w-full items-center justify-between overflow-x-scroll">
          <div className="flex items-end space-x-1">
            <Button
              size="sm"
              className="rounded-full"
              rightIcon={<Lightbulb size={14} />}
              onClick={() => setType(type === "CONTENT" ? null : "CONTENT")}
              variant={type === "CONTENT" ? "primary" : "outline"}
            >
              Content
            </Button>
            <Button
              size="sm"
              className="rounded-full"
              rightIcon={<Folder size={14} />}
              onClick={() => setType(type === "PROJECT" ? null : "PROJECT")}
              variant={type === "PROJECT" ? "primary" : "outline"}
            >
              Projects
            </Button>
            <Button
              size="sm"
              className="rounded-full"
              rightIcon={<User size={14} />}
              onClick={() => setType(type === "ACCOUNT" ? null : "ACCOUNT")}
              variant={type === "ACCOUNT" ? "primary" : "outline"}
            >
              Account
            </Button>
          </div>

          <div className="flex items-center gap-0.5">
            {/* FILTER MENU */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <TooltipIconButton
                  tooltipProps={{ label: "Filter notifications" }}
                  variant="ghost"
                  size="xs"
                  aria-label="filter notifications"
                  icon={<FilterIcon size={14} className={cn(status !== "UNREAD_AND_READ" && "text-blue-500")} />}
                />
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Filter</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setStatus("UNREAD_AND_READ")} className="justify-between">
                  <div className="flex items-center gap-2">
                    <Inbox />
                    <span className="text-sm">Unread & read</span>
                  </div>
                  {status === "UNREAD_AND_READ" && <Check />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatus("UNREAD")} className="justify-between">
                  <div className="flex items-center gap-2">
                    <BellDot />
                    <span className="text-sm">Unread</span>
                  </div>
                  {status === "UNREAD" && <Check />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatus("ARCHIVED")} className="justify-between">
                  <div className="flex items-center gap-2">
                    <Archive />
                    <span className="text-sm">Archived</span>
                  </div>
                  {status === "ARCHIVED" && <Check />}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* ACTION MENU */}

            <DropdownMenu>
              <DropdownMenuTrigger asChild disabled={status !== "UNREAD_AND_READ"}>
                <TooltipIconButton
                  tooltipProps={{ label: "More actions" }}
                  variant="ghost"
                  disabled={status !== "UNREAD_AND_READ"}
                  size="xs"
                  aria-label="more actions"
                  icon={<Ellipsis size={14} />}
                />
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => markAllAsRead()}>
                  <CheckSquare2 />
                  <span className="text-sm">Mark all as read</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => markAllAsArchived()}>
                  <Archive />
                  <span className="text-sm">Archive all</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="h-full space-y-1 overflow-y-scroll pb-2">
          {isLoading ? (
            <div className="flex h-[300px] items-center justify-center">
              <Spinner />
            </div>
          ) : isError ? (
            <div className="flex h-[300px] items-center justify-center">
              <p>Error fetching notifications</p>
            </div>
          ) : (
            <>
              {items?.map((notif) => (
                <NotificationItem key={notif.id} notif={notif} />
              ))}
              {isLoading && (
                <div className="flex items-end justify-center py-4">
                  <Spinner />
                </div>
              )}
              {!isLoading && items?.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center space-y-3 pb-10">
                  <Inbox size={50} className="opacity-50" strokeWidth={1} />
                  <div className="flex max-w-[310px] flex-col items-center justify-center text-center">
                    <p className="font-bold text-base opacity-50">
                      {status === "ARCHIVED" ? "No archived notifications" : "You're all caught up"}
                    </p>
                    <p className="font-medium text-sm opacity-50">
                      {status === "ARCHIVED"
                        ? "Any notifications you archived will show up here."
                        : "You'll be notified here if there's any activity you need to know about."}
                    </p>
                  </div>
                </div>
              ) : null}
              {status === "UNREAD_AND_READ" && !isLoading && items && items.length >= 10 && items.length < 100 && (
                <div className="flex items-center justify-center">
                  <Button isLoading={isRefetching} variant="outline" size="sm" onClick={handleFetchMore}>
                    Show more
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

interface NotificationItemProps {
  notif: RouterOutputs["notification"]["all"]["items"][number]
}

const NotificationItem = React.memo(function _NotificationItem({ notif }: NotificationItemProps) {
  if (!notif.notification) return null
  const notificationType = notif.notification.type
  switch (notificationType) {
    case "NEW_MACROTREND_PROJECT_ITEM":
      return (
        <NewProjectItemNotification
          notif={notif}
          href={`/library/projects/${notif.notification.project?.id}`}
          type="Macro-Trend"
          title={notif.notification.macrotrend?.title}
        />
      )
    case "NEW_MEGATREND_PROJECT_ITEM":
      return (
        <NewProjectItemNotification
          notif={notif}
          href={`/library/projects/${notif.notification.project?.id}`}
          type="Mega-Trend"
          title={notif.notification.megatrend?.title}
        />
      )
    case "NEW_SUBTREND_PROJECT_ITEM":
      return (
        <NewProjectItemNotification
          notif={notif}
          href={`/library/projects/${notif.notification.project?.id}`}
          type="Sub-Trend"
          title={notif.notification.subtrend?.title}
        />
      )
    case "NEW_INNOVATION_PROJECT_ITEM":
      return (
        <NewProjectItemNotification
          notif={notif}
          href={`/library/projects/${notif.notification.project?.id}`}
          type="innovation"
          title={notif.notification.innovation?.title}
        />
      )
    case "NEW_INDUSTRY_PROJECT_ITEM":
      return (
        <NewProjectItemNotification
          notif={notif}
          href={`/library/projects/${notif.notification.project?.id}`}
          type="industry"
          title={notif.notification.industry?.title}
        />
      )
    case "NEW_REGION_PROJECT_ITEM":
      return (
        <NewProjectItemNotification
          notif={notif}
          href={`/library/projects/${notif.notification.project?.id}`}
          type="region"
          title={notif.notification.region?.title}
        />
      )
    case "NEW_REPORT_PROJECT_ITEM":
      return (
        <NewProjectItemNotification
          notif={notif}
          href={`/library/projects/${notif.notification.project?.id}`}
          type="report"
          title={notif.notification.report?.title}
        />
      )
    case "NEW_STAT_PROJECT_ITEM":
      return (
        <NewProjectItemNotification
          notif={notif}
          href={`/library/projects/${notif.notification.project?.id}`}
          type="stat"
          title={`${notif.notification.stat?.title}: ${notif.notification.stat?.subTitle}`}
        />
      )
    case "PROJECT_DELETED":
      return (
        <NotificationWrapper
          notif={notif}
          href={"/projects"}
          content={
            <p>
              <span className="font-bold">{createFullName(notif.notification.initiator)}</span> deleted a shared project you’re
              in: <span className="font-bold">{notif.notification.project?.name}</span>
            </p>
          }
        />
      )
    case "PROJECT_EDITED":
      return (
        <NotificationWrapper
          notif={notif}
          href={`/library/projects/${notif.notification.project?.id}`}
          content={
            <p>
              <span className="font-bold">{createFullName(notif.notification.initiator)}</span> changed project name from{" "}
              <span className="font-bold">{notif.notification.fromValue}</span> to{" "}
              <span className="font-bold">{notif.notification.toValue}</span>
            </p>
          }
        />
      )
    case "USER_ADDED_TO_PROJECT":
      return (
        <NotificationWrapper
          notif={notif}
          href={`/library/projects/${notif.notification.project?.id}`}
          content={
            <p>
              <span className="font-bold">{createFullName(notif.notification.initiator)}</span> added you to the project{" "}
              <span className="font-bold">{notif.notification.project?.name}</span>
            </p>
          }
        />
      )
    case "NEW_REPORT":
      return (
        <NotificationWrapper
          notif={notif}
          href={`/reports/${notif.notification.report?.id}`}
          content={
            <p>
              Trend Analyst <span className="font-bold">{createFullName(notif.notification.initiator)}</span> posted a new report:{" "}
              <span className="font-bold">{notif.notification.report?.title}</span>
            </p>
          }
        />
      )
    case "NEW_DAILY":
      return (
        <NotificationWrapper
          notif={notif}
          href={`/daily/${notif.notification.daily?.slug}`}
          content={
            // New Daily [post type: e.g. Insight or Innovation]: [Title of post]
            <p>
              New Daily{" "}
              {notif.notification.daily?.type === "THOUGHT_STARTER"
                ? "Thought-Starter"
                : capitalize(notif.notification.daily?.type || "")}
              : <span className="font-bold">{notif.notification.daily?.title}</span>
            </p>
          }
        />
      )
    case "NEW_SUBTREND":
      return (
        <NotificationWrapper
          notif={notif}
          href={`/subtrends/${notif.notification.subtrend?.slug}`}
          content={
            <p>
              Trend Analyst, <span className="font-bold">{createFullName(notif.notification.initiator)}</span> published a new
              Sub-Trend: <span className="font-bold">{notif.notification.subtrend?.title}</span>
            </p>
          }
        />
      )
    case "NEW_INDUSTRY_PICK_INNOVATION":
      return (
        <NotificationWrapper
          notif={notif}
          href={`/industries/${notif.notification.innovation?.industry?.slug}#iotm`}
          content={
            <p>
              Trend Analyst, <span className="font-bold">{createFullName(notif.notification.initiator)}</span> published a new
              Industry Pick innovation: <span className="font-bold">{notif.notification.innovation?.title}</span>
            </p>
          }
        />
      )
    case "NEW_REGION_PICK_INNOVATION":
      return (
        <NotificationWrapper
          notif={notif}
          href={`/regions/${notif.notification.innovation?.region?.slug}#iotm`}
          content={
            <p>
              Trend Analyst, <span className="font-bold">{createFullName(notif.notification.initiator)}</span> published a new
              Region Pick innovation: <span className="font-bold">{notif.notification.innovation?.title}</span>
            </p>
          }
        />
      )
    case "NEW_ANALYST_PICK_STAT":
      return (
        <NotificationWrapper
          notif={notif}
          href={`/stats/${notif.notification.stat?.id}`}
          content={
            <p>
              New Stat Pick:{" "}
              <span className="font-bold">
                {notif.notification.stat?.title} {notif.notification.stat?.subTitle}
              </span>
            </p>
          }
        />
      )
    case "NEW_TOPIC":
      return (
        <NotificationWrapper
          notif={notif}
          href={notif.notification.topic?.reportId ? `/reports/${notif.notification.topic?.reportId}` : undefined}
          content={
            <p>
              New Topic: <span className="font-bold">{notif.notification.topic?.title}</span>
            </p>
          }
        />
      )
    case "NEW_COMPANY_USER":
      return (
        <NotificationWrapper
          notif={notif}
          href={"/profile/company"}
          content={
            <p>
              🥳 Yay! Your colleague <span className="font-bold">{createFullName(notif.notification.initiator)}</span> signed up
              to your company <span className="font-bold">{notif.notification.team?.name}</span>
            </p>
          }
        />
      )
    case "NEW_INVOICE":
      return (
        <NotificationWrapper
          notif={notif}
          href={"/company/invoices"}
          content={
            <p>
              A new invoice has been created for your company, view and download it <span className="font-bold">here</span>
            </p>
          }
        />
      )
    case "USER_ROLE_UPDATED":
      if (!notif.notification.toValue) return null
      return (
        <NotificationWrapper
          notif={notif}
          href={(notif.notification?.toValue as Role) === "TEAM_MEMBER" ? "/company/member" : "/profile/company"}
          content={
            <p>
              <b>{createFullName(notif.notification.initiator)}</b> updated your account role to{" "}
              <b>{ROLE[notif.notification?.toValue as Role]}</b>
            </p>
          }
        />
      )
    case "GENERATION_COMPLETED": {
      const generation = notif.notification.generation
      if (!generation) return null
      const toolId = generation.toolId
      // TODO update once other AI tools are done
      switch (toolId) {
        case TRENDBABY_TOOL_IDS.ProductLab: {
          return (
            <NotificationWrapper
              notif={notif}
              href={`/trendbaby/productlab/${generation.id}`}
              content={
                <p>
                  Your ProductLab generation is complete, view it <b>here</b>
                </p>
              }
            />
          )
        }
        default:
          // toolId satisfies never
          return null
      }
    }

    default:
      notificationType satisfies never
      return null
  }
})

interface NotificationWrapperProps {
  notif: RouterOutputs["notification"]["all"]["items"][number]
  href?: string
  content: React.ReactNode
}

const NotificationWrapper = React.memo(function _NotificationWrapper({ href, content, notif }: NotificationWrapperProps) {
  const utils = api.useUtils()

  const { mutate: markAsRead } = api.notification.markAsRead.useMutation({
    onSuccess: () => {
      utils.notification.all.invalidate()
      utils.notification.seenCount.invalidate()
      utils.notification.unreadCount.invalidate()
    },
  })
  const { mutate: markAsUnread } = api.notification.markAsUnread.useMutation({
    onSuccess: () => {
      utils.notification.all.invalidate()
      utils.notification.seenCount.invalidate()
      utils.notification.unreadCount.invalidate()
    },
  })
  const { mutate: markAsArchived } = api.notification.markAsArchived.useMutation({
    onSuccess: () => {
      utils.notification.all.invalidate()
      utils.notification.seenCount.invalidate()
      utils.notification.unreadCount.invalidate()
    },
  })
  const { mutate: markAsSeen } = api.notification.markAsSeen.useMutation({
    onSuccess: () => {
      utils.notification.all.invalidate()
      utils.notification.seenCount.invalidate()
      utils.notification.unreadCount.invalidate()
    },
  })
  const { id, status, createdAt, notification } = notif

  const handleReadNotification = () => {
    if (status === "READ") return
    return markAsRead({ id })
  }

  const handleUnreadNotification = () => {
    if (status === "UNREAD") return
    return markAsUnread({ id })
  }

  const handleArchiveNotification = () => {
    if (status === "ARCHIVED") return
    return markAsArchived({ id })
  }

  const handleUnarchiveNotification = () => {
    if (status === "SEEN") return
    return markAsSeen({ id })
  }

  const renderAvatarIcon = () => {
    if (PROJECT_NOTIFICATION_TYPES.includes(notification.type)) {
      return <Folder size={12} />
    }
    if (ACCOUNT_NOTIFICATION_TYPES.includes(notification.type)) {
      return <User size={12} />
    }
    return <Lightbulb size={12} />
  }

  const avatar =
    notif.notification.daily?.analystAvatar || notif.notification.daily?.analyst?.avatar || notif.notification.initiator?.avatar

  return (
    <MaybeLink href={href} onClick={handleReadNotification}>
      <div className="group relative flex cursor-pointer justify-between space-x-2 rounded-md py-2 pr-4 pl-1 text-sm hover:bg-gray-100 hover:dark:bg-gray-600">
        <div className="flex items-start space-x-2">
          <div className="flex flex-shrink-0 gap-1">
            <div
              className={cn(
                "mt-3 size-2 flex-shrink-0 rounded-full bg-transparent",
                ["UNREAD", "SEEN"].includes(status) && "bg-green-500",
              )}
            />
            <div className="relative flex-shrink-0">
              <Avatar
                size={32}
                src={notification.initiator ? createAssetUrl(avatar) : "/icon-logo.svg"}
                user={notification.initiator}
              />
              <div className="-bottom-1 -right-1 absolute flex size-5 items-center justify-center rounded-full border bg-white dark:bg-gray-600">
                {renderAvatarIcon()}
              </div>
            </div>
          </div>
          <div>
            {content}
            <p
              suppressHydrationWarning
              className={cn("font-medium text-xs", ["UNREAD", "SEEN"].includes(status) ? "text-green-500" : "opacity-50")}
            >
              {dayjs(createdAt).fromNow()}
            </p>
          </div>
        </div>
        <div className="absolute top-1 right-1 hidden rounded-md bg-background p-0.5 group-hover:flex">
          {["UNREAD", "SEEN"].includes(status) ? (
            <TooltipIconButton
              tooltipProps={{ label: "Mark this notification as read" }}
              icon={<CheckSquare2 size={14} />}
              aria-label="mark this notification as read"
              rounded={false}
              variant="ghost"
              size="xs"
              onClick={(e) => {
                e.preventDefault() // prevent propagation of click to linked item
                handleReadNotification()
              }}
            />
          ) : (
            status === "READ" && (
              <TooltipIconButton
                tooltipProps={{ label: "Mark this notification as unread" }}
                icon={<SquareDashed size={14} />}
                aria-label="mark this notification as unread"
                rounded={false}
                variant="ghost"
                size="xs"
                onClick={(e) => {
                  e.preventDefault() // prevent propagation of click to linked item
                  handleUnreadNotification()
                }}
              />
            )
          )}
          {status !== "ARCHIVED" ? (
            <TooltipIconButton
              tooltipProps={{ label: "Archive this notification" }}
              icon={<Archive size={14} />}
              aria-label="arhive this notification"
              rounded={false}
              variant="ghost"
              size="xs"
              onClick={(e) => {
                e.preventDefault()
                handleArchiveNotification()
              }}
            />
          ) : (
            <TooltipIconButton
              tooltipProps={{ label: "Unarchive" }}
              icon={<Undo2 size={14} />}
              aria-label="unarhive this notification"
              rounded={false}
              variant="ghost"
              size="xs"
              onClick={(e) => {
                e.preventDefault() // prevent propagation of click to linked item
                handleUnarchiveNotification()
              }}
            />
          )}
        </div>
      </div>
    </MaybeLink>
  )
})

function MaybeLink({ href, ...props }: { href?: string; children: React.ReactNode; className?: string; onClick: () => void }) {
  return <>{href ? <Link prefetch={false} href={href} {...props} /> : <div {...props} />}</>
}

function NewProjectItemNotification({
  notif,
  type,
  title,
}: Omit<NotificationWrapperProps, "content"> & { type: string; title?: string | null }) {
  return (
    <NotificationWrapper
      notif={notif}
      href={`/library/projects/${notif.notification.project?.id}`}
      content={
        <p>
          <span className="font-bold">{createFullName(notif.notification.initiator)}</span> added a new {type} to the project{" "}
          <span className="font-bold">{notif.notification.project?.name}</span>: <span>{title}</span>
        </p>
      }
    />
  )
}
